import React from "react";
import {
  Box,
  List,
  Typography,
  CircularProgress,
  Alert,
  Paper,
} from "@mui/material";
import FileItem, { TimeDisplayMode } from "./FileItem";
import PaginationControls from "./PaginationControls";
import CustomScrollbars from "../CustomScrollbars";
import { FileItem as FileItemType } from "../../types/interfaces/filesStorage";

interface FileListProps {
  items: FileItemType[];
  loading?: boolean;
  error?: string | null;
  currentLimit: number;
  onLimitChange: (limit: number) => void;
  onItemClick?: (item: FileItemType) => void;
  onDownloadClick?: (item: FileItemType) => void;
  onPreviewClick?: (item: FileItemType) => void;
  onMoreOptionsClick?: (item: FileItemType, event: React.MouseEvent) => void;
  showDownloadButton?: boolean;
  showPreviewButton?: boolean;
  showMoreOptions?: boolean;
  disableMoreOptions?: boolean;
  moreOptionsDisabledTooltip?: string;
  showPagination?: boolean;
  showLimitSelector?: boolean;
  showPageInfo?: boolean;
  disabled?: boolean;
  size?: "small" | "medium" | "large";
  variant?: "default" | "compact" | "detailed";
  emptyMessage?: string;
  maxHeight?: string | number;
  currentPage?: number;
  totalPages?: number;
  totalCount?: number;
  onPageChange?: (page: number) => void;
  pageTokens?: string[];
  downloadingItems?: Set<string>;
  downloadProgress?: Record<string, number | undefined>;
  hasNextPage?: boolean;
  timeDisplayMode?: TimeDisplayMode;
  showFileDate?: boolean;
  hideEmptyLabel?: boolean;
  moreOptionsFoldersOnly?: boolean;
  documentScroll?: boolean;
  disableFileClick?: boolean;
}

const FileList: React.FC<FileListProps> = ({
  items,
  loading = false,
  error = null,
  currentLimit,
  onLimitChange,
  onItemClick,
  onDownloadClick,
  onPreviewClick,
  onMoreOptionsClick,
  showDownloadButton = true,
  showPreviewButton = false,
  showMoreOptions = false,
  disableMoreOptions = false,
  moreOptionsDisabledTooltip = "",
  showPagination = true,
  showLimitSelector = true,
  showPageInfo = true,
  disabled = false,
  size = "medium",
  variant = "default",
  emptyMessage = "No files or folders found",
  maxHeight = "600px",
  currentPage = 1,
  totalPages = 1,
  totalCount,
  onPageChange,
  pageTokens = [],
  downloadingItems = new Set(),
  downloadProgress = {},
  hasNextPage = false,
  timeDisplayMode = "local",
  showFileDate = true,
  hideEmptyLabel = false,
  moreOptionsFoldersOnly = false,
  documentScroll = false,
  disableFileClick = false,
}) => {
  const paginatedItems = items;

  const handleLimitChange = (limit: number) => {
    onLimitChange(limit);
  };

  const renderContent = () => {
    if (loading) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 200,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Box sx={{ p: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      );
    }

    if (items.length === 0) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 200,
            color: "text.secondary",
          }}
        >
          <Typography variant="body1">{emptyMessage}</Typography>
        </Box>
      );
    }

    return (
      <List className="w-full">
        {paginatedItems.map((item, index) => {
          const itemKey = `${item.fsource}-${item.fname}`;
          const isDownloading = downloadingItems.has(itemKey);
          const progress = downloadProgress[itemKey] || 0;

          return (
            <FileItem
              key={item.fsource}
              item={item}
              onItemClick={onItemClick}
              onDownloadClick={onDownloadClick}
              onPreviewClick={onPreviewClick}
              onMoreOptionsClick={onMoreOptionsClick}
              showDownloadButton={showDownloadButton}
              showPreviewButton={showPreviewButton}
              showMoreOptions={showMoreOptions}
              disableMoreOptions={disableMoreOptions}
              moreOptionsDisabledTooltip={moreOptionsDisabledTooltip}
              disabled={disabled}
              isDownloading={isDownloading}
              downloadProgress={progress}
              backgroundColor={
                index % 2 === 0 ? "action.hover" : "background.paper"
              }
              timeDisplayMode={timeDisplayMode}
              showFileDate={showFileDate}
              hideEmptyLabel={hideEmptyLabel}
              moreOptionsFoldersOnly={moreOptionsFoldersOnly}
              disableFileClick={disableFileClick}
            />
          );
        })}
      </List>
    );
  };

  return (
    <Paper
      elevation={documentScroll ? 0 : 1}
      sx={{
        display: "flex",
        flexDirection: "column",
        ...(documentScroll
          ? { height: "auto", overflow: "visible" }
          : {
              flex: "1 1 0",
              minHeight: 0,
              width: "100%",
              overflow: "hidden",
            }),
        borderRadius: documentScroll ? { xs: 0, sm: 2 } : 2,
        boxShadow: documentScroll ? { xs: "none", sm: undefined } : undefined,
        bgcolor: "background.paper",
      }}
    >
      {documentScroll ? (
        <Box sx={{ width: 1 }}>{renderContent()}</Box>
      ) : (
        <CustomScrollbars
          {...(typeof maxHeight === "string" && maxHeight.endsWith("%")
            ? {}
            : { style: { maxHeight } })}
          sx={{
            flex: 1,
            minHeight: 0,
            borderRadius: 1,
            ...(typeof maxHeight === "string" &&
              maxHeight.endsWith("%") && {
                maxHeight: "none",
              }),
          }}
          trackOffsetTop={15}
        >
          {renderContent()}
        </CustomScrollbars>
      )}

      {showPagination && items.length > 0 && (
        <Box
          sx={{
            position: "relative",
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <PaginationControls
            currentLimit={currentLimit}
            onLimitChange={handleLimitChange}
            totalItems={totalCount ?? items.length}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
            showLimitSelector={showLimitSelector}
            showPageInfo={showPageInfo}
            disabled={disabled || loading}
            size={size}
            pageTokens={pageTokens}
            hasNextPage={hasNextPage}
          />
        </Box>
      )}
    </Paper>
  );
};

export default FileList;
